import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '@/shared/auth/AuthContext'
import { ApiError } from '@/shared/api/client'
import { Logo } from '@/shared/ui/Icon'
import { Note } from '@/shared/ui/primitives'
import { FieldText } from '@/shared/forms/fields'
import { homeFor } from '@/app/navigation'

const schema = z.object({
  email: z.string().email('Adresse e-mail invalide'),
  password: z.string().min(1, 'Mot de passe'),
})

type Values = z.infer<typeof schema>

export function LoginPage() {
  const { user, login, booting } = useAuth()
  const nav = useNavigate()
  const [serverError, setServerError] = useState<string | null>(null)

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  if (user) return <Navigate to={homeFor(user.role)} replace />

  const onSubmit = form.handleSubmit(async (v) => {
    setServerError(null)
    try {
      await login(v.email, v.password)
      nav('/', { replace: true })
    } catch (e) {
      if (e instanceof ApiError && e.status === 404)
        setServerError('Ce locataire est introuvable. Vérifiez l’identifiant.')
      else if (e instanceof ApiError && e.status === 429)
        setServerError('Trop de tentatives. Patientez une minute.')
      else setServerError(e instanceof Error ? e.message : 'Connexion impossible.')
    }
  })

  return (
    <div className="auth">
      <div className="auth__brand">
        <Logo className="auth__mark" />
        <div>
          <p className="auth__word">Sahel Fuel Control</p>
          <p className="auth__tag">Pilotage de réseau de stations-service</p>
        </div>
      </div>
      <div className="auth__panel">
        <h1 className="auth__title">Connexion</h1>
        <p className="auth__lead">Console web — accès réservé au personnel du réseau</p>
        {serverError && (
          <Note tone="bad" title="Connexion refusée">
            {serverError}
          </Note>
        )}
        <form onSubmit={onSubmit} noValidate style={{ display: 'grid', gap: 14 }}>
          <FieldText
            label="Adresse e-mail"
            required
            type="email"
            field={form.register('email')}
            placeholder="vous@reseau.ne"
            error={form.formState.errors.email}
          />
          <FieldText
            label="Mot de passe"
            required
            type="password"
            field={form.register('password')}
            error={form.formState.errors.password}
          />
          <button
            type="submit"
            className="btn btn--primary"
            style={{ justifyContent: 'center', padding: 11 }}
            disabled={form.formState.isSubmitting || booting}
          >
            {form.formState.isSubmitting ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>
        <p className="auth__note">
          Le rôle de votre compte détermine la navigation, les droits et l’écran d’accueil. Cinq
          tentatives par minute au maximum.
        </p>
      </div>
    </div>
  )
}
